import { useEffect, useRef, useState } from 'react';

const THRESHOLD = 64;
const MAX_PULL = 96;

// Pull-down-to-refresh for a list pane. Attach `ref` to the pane root and
// render <PullIndicator> at the top of it. Only engages when every
// scrollable ancestor is at its top, so ordinary scrolling is untouched.
export default function usePullToRefresh(onRefresh) {
  const ref = useRef(null);
  const cb = useRef(onRefresh);
  cb.current = onRefresh;
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const busy = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let startY = null;
    let cur = 0;

    const atTop = () => {
      for (let n = el; n && n !== document.body; n = n.parentElement) {
        const oy = window.getComputedStyle(n).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && n.scrollTop > 0) return false;
      }
      return true;
    };

    const onStart = (e) => {
      if (busy.current || !e.touches || e.touches.length !== 1) return;
      startY = atTop() ? e.touches[0].clientY : null;
    };
    const onMove = (e) => {
      if (startY == null || busy.current) return;
      const dy = e.touches[0].clientY - startY;
      if (dy <= 0 || !atTop()) { cur = 0; setPull(0); return; }
      cur = Math.min(dy * 0.5, MAX_PULL);
      setPull(cur);
      if (e.cancelable) e.preventDefault();
    };
    const onEnd = async () => {
      if (startY == null) return;
      startY = null;
      if (cur >= THRESHOLD && !busy.current) {
        busy.current = true;
        setRefreshing(true);
        setPull(48);
        try { await cb.current?.(); } catch (_) { /* caller handles errors */ }
        busy.current = false;
        setRefreshing(false);
      }
      cur = 0;
      setPull(0);
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  return { ref, pull, refreshing, threshold: THRESHOLD };
}
